// Clause tree rendering and inline clause locate links in AI responses.

import { locateByText, locateByNodeId } from "./selection";
import { devLog } from "./messages";
import { activeNodeMap } from "./state";

let _toggleBound = false;

// Clause tree panel removed — kept as no-op for call-site compatibility.
export function renderClauseTree(_clauses) {}

export function renderClauseNode(parent, node, depth) {
  const div = document.createElement("div");
  div.className = "clause-node";
  div.style.paddingLeft = `${6 + depth * 12}px`;

  const num = document.createElement("span");
  num.className = "clause-number";
  num.textContent = node.number || "";

  const titleSpan = document.createElement("span");
  titleSpan.className = "clause-title";
  titleSpan.textContent = node.title || "";

  div.appendChild(num);
  div.appendChild(titleSpan);
  parent.appendChild(div);

  div.addEventListener("click", async (e) => {
    e.stopPropagation();
    let found = false;

    // 1. Try node_ids from tree (most precise — matches exact paragraph)
    if (node.node_ids && node.node_ids.length > 0) {
      for (const nid of node.node_ids) {
        found = await locateByNodeId(nid);
        if (found) break;
      }
    }
    // 2. Try clause's own id
    if (!found && node.id) {
      found = await locateByNodeId(node.id);
    }
    // 3. Fallback: text search
    if (!found) {
      const combined = ((node.number || "") + " " + (node.title || "")).trim();
      if (combined) found = await locateByText(combined);
    }
    if (!found && node.title) found = await locateByText(node.title.trim());
    if (!found && node.number) found = await locateByText(node.number.trim());
  });

  if (node.children) {
    node.children.forEach((child) => renderClauseNode(parent, child, depth + 1));
  }
}

// Inject clickable locate icons next to "Clause 5.1", "clauses 29, 30, and 35" etc.
// Links are created ONLY for clauses the backend provides in referencedClauses (with node_ids).
// Falls back to clauseMap from cached tree if referencedClauses is empty.
export function injectInlineClauseLinks(bodyEl, clauseMap, referencedClauses) {
  if (!bodyEl) return;

  const numberMap = {};

  // Primary source: backend-provided referenced_clauses (authoritative node_ids)
  if (referencedClauses && referencedClauses.length > 0) {
    for (const rc of referencedClauses) {
      if (!rc.number) continue;
      numberMap[rc.number] = {
        number: rc.number,
        title: rc.title || "",
        node_id: rc.node_id || rc.id || "",
        node_ids: rc.node_ids || [],
        start_page: rc.start_page || 0,
        end_page: rc.end_page || 0,
      };
    }
    // devLog(
    //   "CLAUSE_REFS",
    //   `Backend provided ${referencedClauses.length} clauses for linking: ${referencedClauses.map((c) => c.number).join(", ")}`
    // );
  } else if (clauseMap) {
    // Fallback: use cached tree (history messages don't have referencedClauses)
    for (const [, clause] of Object.entries(clauseMap)) {
      if (clause && clause.number) {
        numberMap[clause.number] = clause;
      }
    }
  }

  if (Object.keys(numberMap).length === 0) return;

  const walker = document.createTreeWalker(bodyEl, NodeFilter.SHOW_TEXT, null, false);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);

  // Matches "Clause 5.1", "clauses 15–19", "clauses 29, 30, and 35"
  // Also handles optional @ prefix the AI sometimes emits (e.g. "Clauses @15–19")
  const clauseGroupPattern =
    /\b[Cc]lauses?\s+@?(\d+(?:\.\d+)*)(?:[–—-]@?(\d+(?:\.\d+)*))?(?:\s*(?:,\s*|\s+and\s+|\s+or\s+)\s*@?(\d+(?:\.\d+)*)(?:[–—-]@?(\d+(?:\.\d+)*))?)*\b/g;

  for (const textNode of textNodes) {
    const text = textNode.textContent;
    if (!clauseGroupPattern.test(text)) continue;
    clauseGroupPattern.lastIndex = 0;

    const frag = document.createDocumentFragment();
    let lastIdx = 0;
    let match;

    while ((match = clauseGroupPattern.exec(text)) !== null) {
      const fullMatch = match[0];
      const startIdx = match.index;

      if (startIdx > lastIdx) {
        frag.appendChild(document.createTextNode(text.slice(lastIdx, startIdx)));
      }

      const keywordMatch = fullMatch.match(/^[Cc]lauses?\s+/);
      const keyword = keywordMatch ? keywordMatch[0] : "";
      if (keyword) frag.appendChild(document.createTextNode(keyword));

      // Strip keyword and any leading @ from the number portion
      let numPart = fullMatch.slice(keyword.length).replace(/^@/, "");
      let numPartIdx = 0;

      // Tokenise into ranges (NUM–NUM) and individual numbers
      const tokenRe = /(\d+(?:\.\d+)*)(?:\s*[–—-]\s*@?(\d+(?:\.\d+)*))?/g;
      let tok;
      while ((tok = tokenRe.exec(numPart)) !== null) {
        const tokStart = tok.index;
        const startNum = tok[1];
        const endNum = tok[2]; // defined only for ranges like 15–19

        // Output any separator text before this token (commas, "and", etc.)
        if (tokStart > numPartIdx) {
          frag.appendChild(document.createTextNode(numPart.slice(numPartIdx, tokStart)));
        }

        if (endNum) {
          // Range — show "startNum–endNum" as a single link anchored to whichever
          // endpoint the backend knows about (start preferred, end as fallback).
          const anchor = numberMap[startNum] || numberMap[endNum];
          if (anchor) {
            frag.appendChild(_makeClauseSpan(`${startNum}–${endNum}`, anchor));
          } else {
            frag.appendChild(document.createTextNode(tok[0]));
          }
        } else {
          if (numberMap[startNum]) {
            frag.appendChild(_makeClauseSpan(startNum, numberMap[startNum]));
          } else {
            frag.appendChild(document.createTextNode(startNum));
          }
        }

        numPartIdx = tokStart + tok[0].length;
      }

      if (numPartIdx < numPart.length) {
        frag.appendChild(document.createTextNode(numPart.slice(numPartIdx)));
      }

      lastIdx = startIdx + fullMatch.length;
    }

    if (lastIdx < text.length) {
      frag.appendChild(document.createTextNode(text.slice(lastIdx)));
    }

    textNode.parentNode.replaceChild(frag, textNode);
  }
}

// Create clickable clause pill button.
// displayLabel is what's shown (e.g. "15–19" for ranges); clause is the anchor clause object.
function _makeClauseSpan(displayLabel, clause) {
  const span = document.createElement("span");
  span.className = "inline-clause-ref";

  const pageHint =
    clause && clause.start_page
      ? ` (p${clause.start_page}${clause.end_page && clause.end_page !== clause.start_page ? "-" + clause.end_page : ""})`
      : "";
  span.title = clause
    ? `${clause.number}${clause.title ? " — " + clause.title : ""}${pageHint}`.trim()
    : `Locate Clause ${displayLabel} in document`;
  span.textContent = "@" + displayLabel;

  const capturedNum = clause?.number || displayLabel;
  const capturedClause = clause;
  span.addEventListener("click", async (e) => {
    e.preventDefault();
    e.stopPropagation();
    let found = false;

    // 1. Try backend-provided node_id first (most precise)
    if (!found && capturedClause && capturedClause.node_id) {
      found = await locateByNodeId(capturedClause.node_id);
    }
    // 2. Try node_ids array from tree
    if (!found && capturedClause && capturedClause.node_ids && capturedClause.node_ids.length > 0) {
      for (const nid of capturedClause.node_ids) {
        found = await locateByNodeId(nid);
        if (found) break;
      }
    }
    // 3. Fallback: text search for "number title"
    if (!found && capturedClause) {
      const searchText = (
        (capturedClause.number || "") +
        " " +
        (capturedClause.title || "")
      ).trim();
      if (searchText) found = await locateByText(searchText);
    }
    // 3.5. Try full node text from activeNodeMap (more specific than "Clause X")
    if (!found && capturedClause && capturedClause.node_id) {
      const nodeText = activeNodeMap[capturedClause.node_id]?.text;
      if (nodeText) found = await locateByText(nodeText);
    }
    // 4. Last resort: search "Clause X"
    if (!found) {
      await locateByText(`Clause ${capturedNum}`);
    }
  });

  return span;
}

// Build flat map of nodeId/number → clause from the tree
export function _buildClauseMap(clauses) {
  const map = {};
  function walk(nodes) {
    for (const n of nodes) {
      if (n.id) map[n.id] = n;
      if (n.node_ids) {
        for (const nid of n.node_ids) {
          map[nid] = n;
        }
      }
      // Always index by number so injectInlineClauseLinks can find clauses
      // even when the slim tree has no id/node_ids
      if (n.number) map[`num:${n.number}`] = n;
      if (n.children) walk(n.children);
    }
  }
  walk(clauses);
  return map;
}
